import { useCallback, useEffect, useMemo, useState } from "react";
import { getClinicalTerms, removeClinicalTerm } from "@/lib/api";

/**
 * The Rx pad's complaint, diagnosis and test suggestions. The list lives in the database (clinical_terms):
 * the shipped terms with what people type for them (abbreviations, Hinglish, lay words), ICD-10 codes for
 * diagnoses, who they can apply to, plus each doctor's own entries and how often the doctor writes each.
 */
export type TermKind = "complaint" | "diagnosis" | "test";

export interface ClinicalTerm {
  id: number;
  name: string;
  category: string | null;
  /** ICD-10 code (diagnoses) */
  code: string | null;
  sex: "F" | "M" | null;
  minAgeMonths: number | null;
  /** Exclusive */
  maxAgeMonths: number | null;
  aliases: string[];
  /** The doctor's own entry (can be removed from their suggestions) */
  own: boolean;
  /** Times the doctor wrote it in the last year */
  uses: number;
}

export interface Patient { sex?: string | null; ageMonths?: number | null }

export interface TermMatch { term: ClinicalTerm; alias?: string }

/** Can this term apply to the patient? Unknown sex or age never hides anything */
export function fitsPatient(t: ClinicalTerm | undefined, p: Patient): boolean {
  if (!t) return true;
  const sex = p.sex?.trim().charAt(0).toUpperCase();
  if (t.sex && (sex === "F" || sex === "M") && t.sex !== sex) return false;
  if (p.ageMonths == null || Number.isNaN(p.ageMonths)) return true;
  if (t.minAgeMonths != null && p.ageMonths < t.minAgeMonths) return false;
  if (t.maxAgeMonths != null && p.ageMonths >= t.maxAgeMonths) return false;
  return true;
}

// Offered in the empty box after the doctor's own most-used entries: by the patient's age, else the specialty
const QUICK_PICKS: Record<TermKind, Record<string, string[]>> = {
  complaint: {
    default:          ["Fever", "Cough (Dry)", "Cold / Runny Nose", "Sore Throat", "Headache", "Body Ache", "Loose Motions", "Burning Sensation (Acidity)"],
    infant:           ["Fever", "Cold / Runny Nose", "Cough (Dry)", "Loose Motions", "Vomiting", "Poor Feeding", "Crying Excessively", "Skin Rash"],
    child:            ["Fever", "Cough (Dry)", "Cold / Runny Nose", "Loose Motions", "Vomiting", "Abdominal Pain", "Sore Throat", "Ear Pain"],
    Gynecology:       ["Missed Period", "Irregular Periods", "Vaginal Discharge", "Painful Periods", "Heavy Periods", "Lower Abdominal Pain", "Suspected Pregnancy", "Pelvic Pain"],
    Orthopedics:      ["Back Pain", "Knee Pain", "Neck Pain", "Shoulder Pain", "Joint Pain", "Injury", "Heel Pain", "Swollen Joint"],
    ENT:              ["Ear Pain", "Ear Discharge", "Sore Throat", "Blocked Nose", "Cold / Runny Nose", "Hearing Loss", "Nose Bleed", "Hoarseness"],
    Ophthalmology:    ["Red Eye", "Blurred Vision", "Watering Eyes", "Eye Pain", "Itchy Eyes", "Eye Discharge", "Foreign Body in Eye", "Eyelid Swelling / Stye"],
    Dermatology:      ["Itching", "Skin Rash", "Pimples / Acne", "Hair Loss", "Fungal Infection / Ringworm", "Hives", "Dark Patches / Pigmentation", "Dandruff"],
    Cardiology:       ["Chest Pain", "Shortness of Breath", "Palpitations", "Swelling (Legs / Face)", "Dizziness", "BP Follow-up", "Fainting / Syncope", "Chest Tightness"],
    Endocrinology:    ["Diabetes Follow-up", "Thyroid Follow-up", "Excessive Thirst", "Frequent Urination", "Weight Gain", "Weight Loss", "Fatigue / Weakness", "Hair Loss"],
    Gastroenterology: ["Abdominal Pain", "Burning Sensation (Acidity)", "Bloating / Gas", "Constipation", "Loose Motions", "Vomiting", "Blood in Stool", "Jaundice"],
    Pulmonology:      ["Cough (Dry)", "Cough (Productive)", "Shortness of Breath", "Wheezing", "Chest Pain", "Fever", "Cough with Blood", "Snoring"],
    Nephrology:       ["Swelling (Legs / Face)", "Reduced Urine Output", "Burning Urination", "Blood in Urine", "Flank / Kidney Pain", "BP Follow-up", "Frequent Urination", "Fatigue / Weakness"],
    Neurology:        ["Headache", "Dizziness", "Numbness / Tingling", "Convulsions", "Weakness of One Side", "Tremors", "Memory Loss", "Vertigo"],
    Psychiatry:       ["Sleep Problems", "Anxiety", "Low Mood / Depression", "Stress", "Panic Attacks", "Memory Loss", "Quit Smoking / Alcohol", "Excessive Sleepiness"],
    Surgery:          ["Abdominal Pain", "Wound / Cut", "Abscess / Boil", "Piles / Anal Swelling", "Groin Swelling / Hernia", "Injury", "Breast Lump", "Swollen Glands"],
  },
  diagnosis: {
    default:          ["Viral Fever", "Upper Respiratory Tract Infection", "Acute Gastroenteritis", "Gastritis", "Essential Hypertension", "Type 2 Diabetes Mellitus", "Urinary Tract Infection", "Allergic Rhinitis"],
    infant:           ["Viral Fever", "Upper Respiratory Tract Infection", "Acute Gastroenteritis", "Bronchiolitis", "Infantile Colic", "Napkin Dermatitis", "Acute Otitis Media", "Neonatal Jaundice"],
    child:            ["Viral Fever", "Upper Respiratory Tract Infection", "Acute Gastroenteritis", "Acute Tonsillitis", "Acute Otitis Media", "Worm Infestation", "Recurrent Wheezing", "Viral Exanthem"],
    Gynecology:       ["Pregnancy — First Trimester", "Polycystic Ovarian Syndrome", "Dysmenorrhoea", "Abnormal Uterine Bleeding", "Leucorrhoea", "Vaginal Candidiasis", "Uterine Fibroid", "Menopausal Syndrome"],
    Orthopedics:      ["Low Back Pain", "Osteoarthritis of Knee", "Cervical Spondylosis", "Frozen Shoulder", "Plantar Fasciitis", "Sprain", "Fracture", "Lumbar Disc Prolapse"],
    ENT:              ["Acute Pharyngitis", "Acute Tonsillitis", "Allergic Rhinitis", "Sinusitis", "Acute Otitis Media", "Impacted Ear Wax", "Otitis Externa", "Deviated Nasal Septum"],
    Ophthalmology:    ["Conjunctivitis", "Allergic Conjunctivitis", "Refractive Error", "Cataract", "Dry Eye Syndrome", "Stye", "Glaucoma", "Presbyopia"],
    Dermatology:      ["Dermatophytosis", "Acne Vulgaris", "Urticaria", "Eczema", "Scabies", "Psoriasis", "Hair Loss", "Melasma"],
    Cardiology:       ["Essential Hypertension", "Ischemic Heart Disease", "Heart Failure", "Dyslipidaemia", "Atrial Fibrillation", "Angina Pectoris", "Palpitations", "Type 2 Diabetes Mellitus"],
    Endocrinology:    ["Type 2 Diabetes Mellitus", "Hypothyroidism", "Prediabetes", "Obesity", "Polycystic Ovarian Syndrome", "Vitamin D Deficiency", "Hyperthyroidism", "Dyslipidaemia"],
    Gastroenterology: ["Gastroesophageal Reflux Disease", "Gastritis", "Irritable Bowel Syndrome", "Fatty Liver", "Acute Gastroenteritis", "Constipation", "Peptic Ulcer Disease", "Haemorrhoids"],
    Pulmonology:      ["Asthma", "COPD", "Pneumonia", "Pulmonary Tuberculosis", "Acute Bronchitis", "Allergic Rhinitis", "Interstitial Lung Disease", "Obstructive Sleep Apnoea"],
    Nephrology:       ["Chronic Kidney Disease", "Urinary Tract Infection", "Renal Calculus", "Essential Hypertension", "Diabetic Nephropathy", "Nephrotic Syndrome", "Acute Kidney Injury", "Hyperuricaemia"],
    Neurology:        ["Migraine", "Tension Headache", "Epilepsy", "Vertigo", "Peripheral Neuropathy", "Cerebrovascular Accident", "Bell's Palsy", "Parkinson's Disease"],
    Psychiatry:       ["Depression", "Anxiety Disorder", "Insomnia", "Panic Disorder", "Bipolar Disorder", "Alcohol Dependence", "Obsessive-Compulsive Disorder", "Schizophrenia"],
    Surgery:          ["Inguinal Hernia", "Haemorrhoids", "Anal Fissure", "Cholelithiasis", "Abscess", "Acute Appendicitis", "Wound", "Cellulitis"],
  },
  test: {
    default:          ["CBC", "Urine R/M", "FBS", "PPBS", "HbA1c", "Lipid Profile", "LFT", "KFT"],
    infant:           ["CBC", "CRP", "Urine R/M", "Serum Bilirubin", "Blood Culture", "Newborn Screening", "Stool R/M", "Chest X-Ray PA View"],
    child:            ["CBC", "CRP", "Urine R/M", "Stool R/M", "Dengue NS1 Antigen", "Widal Test", "Chest X-Ray PA View", "Vitamin D Level"],
    Gynecology:       ["CBC", "Urine Pregnancy Test", "Beta HCG", "Pelvic USG", "Thyroid Profile (T3 T4 TSH)", "Obstetric USG", "Pap Smear", "Prolactin"],
    Orthopedics:      ["X-Ray (specify part)", "Vitamin D Level", "Serum Calcium", "Serum Uric Acid", "RA Factor", "CRP", "MRI (specify part)", "Bone Density (DEXA Scan)"],
    ENT:              ["CBC", "X-Ray PNS", "Pure Tone Audiometry", "Tympanometry", "Throat Swab C/S", "Absolute Eosinophil Count", "CT Scan (specify part)", "Random Blood Sugar"],
    Ophthalmology:    ["Random Blood Sugar", "Fundus Examination", "Intraocular Pressure", "CBC", "HbA1c", "FBS", "ESR", "Thyroid Profile (T3 T4 TSH)"],
    Dermatology:      ["KOH Mount", "CBC", "Absolute Eosinophil Count", "Thyroid Profile (T3 T4 TSH)", "Serum Ferritin", "Vitamin D Level", "Random Blood Sugar", "LFT"],
    Cardiology:       ["ECG", "2D Echo", "Lipid Profile", "Troponin I", "TMT", "HbA1c", "KFT", "NT-proBNP"],
    Endocrinology:    ["HbA1c", "FBS", "PPBS", "Thyroid Profile (T3 T4 TSH)", "TSH", "Lipid Profile", "Vitamin D Level", "Urine ACR"],
    Gastroenterology: ["LFT", "USG Abdomen", "Upper GI Endoscopy", "Stool R/M", "HBsAg", "Anti-HCV", "Serum Lipase", "H. pylori Stool Antigen"],
    Pulmonology:      ["Chest X-Ray PA View", "PFT (Spirometry)", "CBC", "Sputum AFB", "CBNAAT / TrueNat", "HRCT Chest", "Absolute Eosinophil Count", "ESR"],
    Nephrology:       ["KFT", "Serum Electrolytes", "Urine R/M", "Urine ACR", "USG KUB", "CBC", "Serum Uric Acid", "Serum Calcium"],
    Neurology:        ["MRI Brain", "CT Brain Plain", "EEG", "NCV", "Vitamin B12", "Vitamin D Level", "Lipid Profile", "HbA1c"],
    Psychiatry:       ["CBC", "TSH", "Vitamin B12", "LFT", "KFT", "Random Blood Sugar", "Lipid Profile", "ECG"],
    Surgery:          ["CBC", "BT/CT", "PT/INR", "Random Blood Sugar", "HIV 1 & 2", "HBsAg", "USG Abdomen", "Blood Group & Rh"],
  },
};

// Specialties whose patients' complaints don't follow age (an eye is an eye at any age)
const AGE_INDEPENDENT = ["Ophthalmology", "ENT", "Dermatology"];

function quickPickNames(kind: TermKind, specialty: string | undefined, p: Patient): string[] {
  const picks = QUICK_PICKS[kind];
  const years = p.ageMonths == null ? null : p.ageMonths / 12;
  const key = years != null && years < 2 ? "infant"
    : (years != null && years < 13 && !AGE_INDEPENDENT.includes(specialty ?? "")) || (years == null && specialty === "Pediatrics") ? "child"
    : specialty && picks[specialty] ? specialty
    : "default";
  return picks[key];
}

/**
 * Terms matching what's typed, best first: the name, then the name starts with it, an alias starts with
 * it, a word of the name starts with it, the name contains it, an alias contains it. Within a tier, what
 * the doctor writes most comes first.
 */
export function search(terms: ClinicalTerm[], typed: string, p: Patient = {}): TermMatch[] {
  const t = typed.trim().toLowerCase();
  if (!t) return [];
  const scored: { m: TermMatch; tier: number; i: number }[] = [];
  terms.forEach((term, i) => {
    if (!fitsPatient(term, p)) return;
    const n = term.name.toLowerCase();
    let tier = -1;
    let alias: string | undefined;
    if (n === t) tier = 0;
    else if (n.startsWith(t)) tier = 1;
    else if ((alias = term.aliases.find((a) => a.startsWith(t)))) tier = 2;
    else if (n.split(/[\s/()-]+/).some((w) => w.startsWith(t))) tier = 3;
    else if (n.includes(t)) tier = 4;
    else if (t.length >= 3 && (alias = term.aliases.find((a) => a.includes(t)))) tier = 5;
    if (tier >= 0) scored.push({ m: { term, alias }, tier, i });
  });
  return scored
    .sort((a, b) => a.tier - b.tier || b.m.term.uses - a.m.term.uses || a.i - b.i)
    .map((s) => s.m);
}

const cache = new Map<TermKind, ClinicalTerm[]>();

/**
 * One kind's suggestion list. Shown from the page's copy at once; each box refreshes it in the
 * background when it mounts, so entries and usage from the last save show on the next prescription.
 */
export function useClinicalTerms(kind: TermKind) {
  const [terms, setTerms] = useState<ClinicalTerm[]>(() => cache.get(kind) ?? []);

  useEffect(() => {
    let live = true;
    getClinicalTerms(kind)
      .then((r) => {
        if (!Array.isArray(r)) return;
        cache.set(kind, r);
        if (live) setTerms(r);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [kind]);

  /** Takes one of the doctor's own entries out of their suggestions */
  const forget = useCallback(async (id: number) => {
    await removeClinicalTerm(id);
    const next = (cache.get(kind) ?? []).filter((t) => t.id !== id);
    cache.set(kind, next);
    setTerms(next);
  }, [kind]);

  return useMemo(() => {
    const byName = new Map<string, ClinicalTerm>();
    const byAlias = new Map<string, ClinicalTerm>();
    const words = new Set<string>();
    for (const t of terms) {
      for (const w of t.name.toLowerCase().split(/[\s/()-]+/)) if (w) words.add(w);
      const key = t.name.toLowerCase();
      if (!byName.has(key) || !t.own) byName.set(key, t);
      if (!t.own) for (const a of t.aliases) byAlias.set(a, t);
    }
    // The doctor's most-written entries first, from the last year
    const frequent = terms.filter((t) => t.uses >= 2).sort((a, b) => b.uses - a.uses).slice(0, 20);

    return {
      forget,
      /** The term whose name or alias was typed exactly ("bukhar" → Fever, "htn" → Essential Hypertension) */
      find: (typed: string): ClinicalTerm | undefined => {
        const t = typed.trim().toLowerCase();
        return byName.get(t) ?? byAlias.get(t);
      },
      /** Whether what's typed is a whole word of some term ("pain" is; "fev" is only the start of one) */
      isWord: (typed: string): boolean => words.has(typed.trim().toLowerCase()),
      /**
       * What to offer: for an empty box the doctor's most-used entries and the usual ones for this
       * patient, else matches for what's typed. Terms that can't apply to the patient and names already
       * on the prescription (`taken`, lowercased) are left out.
       */
      suggest: (typed: string, p: Patient, specialty: string | undefined, taken: Set<string>, limit = 8): TermMatch[] => {
        const matches: TermMatch[] = typed.trim()
          ? search(terms, typed, p)
          : [...frequent, ...quickPickNames(kind, specialty, p).map((n) => byName.get(n.toLowerCase())).filter((t): t is ClinicalTerm => !!t)]
              .filter((t, i, all) => all.indexOf(t) === i && fitsPatient(t, p))
              .map((term) => ({ term }));
        return matches.filter((m) => !taken.has(m.term.name.toLowerCase())).slice(0, limit);
      },
    };
  }, [terms, kind, forget]);
}
